import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import i18next from 'i18next';

const BO_LANGUAGE_STORAGE_KEY = 'boLanguage';
type TBoLanguage = 'de' | 'en';

const normalizeBoLanguage = (lang?: string | null): TBoLanguage => {
  if (!lang) return 'de';
  return String(lang).toLowerCase().startsWith('en') ? 'en' : 'de';
};

const languageOptions: Array<{ code: TBoLanguage; name: string; flag: string }> = [
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
];

/**
 * BO language selector mirroring user-side UX (button + modal picker).
 * Changes i18next UI language only and persists choice locally.
 */
export default function BoLanguageSelector() {
  const [isOpen, setIsOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState<TBoLanguage>(normalizeBoLanguage(i18next.language));

  useEffect(() => {
    const storedLanguage = localStorage.getItem(BO_LANGUAGE_STORAGE_KEY);
    const languageToUse = normalizeBoLanguage(storedLanguage || i18next.language);
    if (normalizeBoLanguage(i18next.language) !== languageToUse) {
      i18next.changeLanguage(languageToUse);
    }
    setCurrentLanguage(languageToUse);
  }, []);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const onLanguageChanged = (lng: string) => {
      setCurrentLanguage(normalizeBoLanguage(lng));
    };
    i18next.on('languageChanged', onLanguageChanged);
    return () => {
      i18next.off('languageChanged', onLanguageChanged);
    };
  }, []);

  const changeLanguage = (lang: TBoLanguage) => {
    localStorage.setItem(BO_LANGUAGE_STORAGE_KEY, lang);
    i18next.changeLanguage(lang);
    setCurrentLanguage(lang);
    setIsOpen(false);
  };

  const current = languageOptions.find(l => l.code === currentLanguage) || languageOptions[0];

  const dialog = isOpen && mounted ? (
    <>
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          transition: 'opacity 200ms',
          zIndex: 9999,
        }}
        onClick={() => setIsOpen(false)}
      />
      <div
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
          zIndex: 9999,
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            background: 'white',
            borderRadius: 12,
            boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
            width: '100%',
            maxWidth: 420,
            padding: 24,
            position: 'relative',
            pointerEvents: 'auto',
          }}
          onClick={e => e.stopPropagation()}
        >
          <button
            onClick={() => setIsOpen(false)}
            aria-label={String(i18next.t('close'))}
            style={{
              position: 'absolute',
              top: 10,
              right: 10,
              border: 'none',
              background: 'transparent',
              color: '#9ca3af',
              cursor: 'pointer',
              fontSize: 20,
              lineHeight: 1,
            }}
          >
            ×
          </button>

          <h2 style={{ margin: '0 0 20px', fontSize: 28, fontWeight: 600, color: '#1f2937' }}>
            {i18next.t('language-select-title')}
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {languageOptions.map(language => {
              const active = language.code === currentLanguage;
              return (
                <button
                  key={language.code}
                  onClick={() => changeLanguage(language.code)}
                  style={{
                    width: '100%',
                    padding: '16px',
                    borderRadius: 10,
                    border: active ? '2px solid #ac2a6e' : '2px solid #e5e7eb',
                    background: active ? '#fdf2f8' : '#fff',
                    transition: 'all 160ms ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <span style={{ fontSize: 34 }}>{language.flag}</span>
                    <div style={{ fontSize: 18, fontWeight: 500, color: active ? '#ac2a6e' : '#1f2937' }}>{language.name}</div>
                  </div>
                  {active && <span style={{ color: '#ac2a6e', fontWeight: 700 }}>✓</span>}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </>
  ) : null;

  return (
    <>
      <div style={{ marginRight: 20, marginLeft: 6 }}>
        <button
          onClick={() => setIsOpen(true)}
          aria-label={String(i18next.t('language-select-title'))}
          onMouseOver={() => setHover(true)}
          onMouseOut={() => setHover(false)}
          style={{
            border: 'none',
            background: 'transparent',
            color: 'inherit',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 2 }}>
              <svg
                width="26"
                height="26"
                viewBox="0 0 24 24"
                fill="none"
                stroke={hover ? '#ac2a6e' : '#333'}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="2" y1="12" x2="22" y2="12"></line>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
              </svg>
            </div>
            <div style={{ fontSize: 12 }}>{current.name}</div>
          </div>
        </button>
      </div>

      {mounted && createPortal(dialog, document.body)}
    </>
  );
}
